import { Option } from '@sapphire/result';

import { BasePayloadMapper } from '@ps/utils';
import {
  distributeCompletes,
  SupplierAllocation,
  SupplierAllocationsUpdate,
} from '@ps/survey-utils';

import {
  SupplierAllocationsUpdateMemberDto,
  SupplierAllocationsUpdateRowDto,
} from '../dtos/supplier.dto';

export class SupplierAllocationsBodyPayloadMapper extends BasePayloadMapper<
  SupplierAllocationsUpdate,
  SupplierAllocationsUpdateRowDto[]
> {
  /** Narrow to the enabled rows, split the goal across them, then shape each row. */
  public toPayload(
    update: SupplierAllocationsUpdate,
  ): SupplierAllocationsUpdateRowDto[] {
    const rows = this._enabledRows(update);
    const quantities = distributeCompletes(
      update.context.goal,
      rows.map((allocation) => this._rowId(allocation)),
    );

    return rows.map((allocation) =>
      this._toRow(allocation, quantities, update),
    );
  }

  /** Only the suppliers still checked at Apply time, in the order they were shown. */
  private _enabledRows({
    allocations,
    enabledSupplierIds,
  }: SupplierAllocationsUpdate): readonly SupplierAllocation[] {
    const enabled = new Set(enabledSupplierIds);
    return allocations.filter((allocation) =>
      enabled.has(this._rowId(allocation)),
    );
  }

  private _toRow(
    allocation: SupplierAllocation,
    quantities: ReadonlyMap<string, number>,
    update: SupplierAllocationsUpdate,
  ): SupplierAllocationsUpdateRowDto {
    // `quantities` was keyed off these very rows above, so the lookup always
    // hits. The 0 only satisfies the type — a miss should read as obviously
    // unallocated rather than as a plausible goal.
    const quantity = quantities.get(this._rowId(allocation)) ?? 0;

    return {
      bid_cpi: allocation.bidCpi,
      completes_goal: allocation.completesGoal,
      cpi: this._cpiFor(allocation, update),
      current_target: quantity,
      flexValue: allocation.flexValue,
      group_name: allocation.isGroup ? allocation.groupName : '',
      quantity,
      suppliers: this._membersFor(allocation),
    };
  }

  /**
   * A global override wins for every row regardless of individual edits;
   * otherwise this row's own edited rate applies if there is one, else the
   * allocation keeps whatever CPI it already had.
   */
  private _cpiFor(
    allocation: SupplierAllocation,
    { context, supplierRates }: SupplierAllocationsUpdate,
  ): number {
    return context.globalOverrideValue.unwrapOr(
      Option.from(supplierRates.get(this._rowId(allocation))).unwrapOr(
        allocation.cpi,
      ),
    );
  }

  private _rowId(allocation: SupplierAllocation): string {
    return allocation.isGroup ? allocation.groupId : String(allocation.id);
  }

  private _membersFor(
    allocation: SupplierAllocation,
  ): SupplierAllocationsUpdateMemberDto[] {
    return allocation.isGroup
      ? allocation.suppliers.map((member) => ({
          id: member.id,
          name: member.name,
          allocation: member.allocation,
        }))
      : [{ id: allocation.id, name: allocation.name, allocation: 100 }];
  }
}
